import type { FC } from 'react';

import { useTranslation } from '@crehana/i18n';
import DifficultyMedal from '@crehana/icons/DifficultyMedal';
import ArrowLeft from '@crehana/icons/outline/ArrowLeft';
import ArrowUp from '@crehana/icons/outline/ArrowUp';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { Col, Container, colors, Row } from '@crehana/ui';

import Chart from '../../../../components/Chart';
import type { ResultsChartProps } from './types';

const { grayMain, baseLighter, secondaryMain } = colors;

const ResultsChart: FC<React.PropsWithChildren<ResultsChartProps>> = ({
  me,
  yAxisPreviousScore,
  xAxisPreviousScore,
  yAxisScore,
  xAxisScore,
  level,
  slug,
  goBack,
}) => {
  const { t } = useTranslation();

  return (
    <Container className="py-40 lg:py-80">
      <button
        type="button"
        className="flex items-center md:mb-48 mb-24"
        onClick={goBack}
      >
        <ArrowLeft color={secondaryMain} size={20} />
        <p className="uppercase font-label ml-8 text-secondary-main">
          {t('GO_BACK_CTA')}
        </p>
      </button>
      <p className="font-h2 text-white w-full md:w-11/12 lg:w-8/12 mb-32 lg:mb-80">
        {t('RESULTS_TITLE1', {
          interpolation: {
            name: me?.firstName || me?.username,
          },
        })}
        <br />
        {t('RESULTS_TITLE2')}&nbsp;
        <span className="text-secondary-main">{slug}</span>
      </p>
      <Row className="flex-col md:flex-row">
        <Col lg={8} md={12} xs={12} className="py-0">
          <Chart
            metricsResult={
              yAxisPreviousScore && xAxisPreviousScore
                ? [
                    {
                      cx: xAxisPreviousScore,
                      cy: yAxisPreviousScore,
                      label: t('RESULTS_PREV'),
                    },
                    {
                      cx: xAxisScore,
                      cy: yAxisScore,
                      label: t('RESULTS_NOW'),
                    },
                  ]
                : [
                    {
                      cx: xAxisScore,
                      cy: yAxisScore,
                      label: t('RESULTS_NOW'),
                    },
                  ]
            }
          />
        </Col>
        <Col
          offset={{ lg: 1 }}
          lg={3}
          md={12}
          xs={12}
          className="mt-32 md:mt-0"
        >
          <Row>
            <Col lg={12} sm={3} xs={6}>
              <p className="text-base-lighter font-label mb-8">
                {t('RESULTS_LEVEL')}
              </p>
              <div className="flex items-center">
                <DifficultyMedal size={24} color={grayMain} />
                <p className="font-h4 text-white ml-12">{level}</p>
              </div>
              {yAxisPreviousScore < yAxisScore ? (
                <div className="flex items-center mt-4">
                  <ArrowUp size={16} color={baseLighter} />
                  <p className="font-subtitle4 ml-4 text-base-lighter">
                    {t('RESULTS_LEVEL_UP')}
                  </p>
                </div>
              ) : null}
            </Col>
            <Col lg={12} sm={3} xs={5} offset={{ xs: 1, sm: 0 }}>
              <p className="font-label text-base-lighter mb-8">
                {t('RESULTS_POINTS')}
              </p>
              <p className="font-h4 text-secondary-main">
                {t('RESULTS_POINT', {
                  interpolation: {
                    score: yAxisScore,
                  },
                })}
              </p>
            </Col>
            <Col lg={12} md={5} sm={6}>
              <p className="font-body4 text-white mt-24 sm:mt-0">
                {t('RESULTS_REDO_TEST')}
              </p>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default ResultsChart;
